import { httpJson, withTimeout } from '@/lib/compat'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PUBLİK PARAMETRLƏR — `GET /api/settings/public` (anonim, auth YOXDUR)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sahib tələbi (2026-08-30): «canlı modulunu … admin paneldən aktivləşdirmə
 * modulu qoymaq lazımdır ki, menyuda görünüb görünməməsini admin paneldən
 * müəyyən edək. bütün applar və saytda hər yerdə canlı menusu görünüb
 * görünməməsi settings-dən müəyyən edilsin ki, hələ ki deaktiv olacaq».
 *
 * ⚠⚠ DEFOLT «BAĞLI»DIR VƏ BU, TƏSADÜFİ DEYİL: CAVAB GƏLƏNDƏ bənd yalnız dəyər
 *    HƏRFİ «true» olanda açılır — açar cavabda yoxdursa, dəyər `null`, «False»
 *    və ya boşdursa BAĞLI. Keşi olmayan cihazda (İLK AÇILIŞ, `readCached()` →
 *    `false`) da bölmə BAĞLI gəlir. «Bilmirəmsə aç» defoltu heç yerdə yoxdur.
 *
 * ⚠⚠ SORĞU UĞURSUZ OLANDA (timeout · 5xx · şəbəkə çuxuru) DƏYƏR DƏYİŞMİR — SON
 *    BİLİNƏN keşlənmiş dəyər qalır (`catch` aşağıdadır). Bu, qəsdi qərardır:
 *    uğursuz sorğu heç nə ÖYRƏTMİR, onu «söndür» kimi oxusaydıq sahibin AÇDIĞI
 *    bölmə bir şəbəkə çuxurunda televizorun menyusundan itərdi (Android-də məhz
 *    bu ölçülüb: bənd çubuqdan silinir və istifadəçi «Canlı»dan Ana ekrana
 *    atılır). Təhlükə yaratmır, çünki qapını SERVER saxlayır — açar bağlı olanda
 *    bilet ucu sorğunu hər halda rədd edir. Eyni qərar iOS
 *    `Shared/Services/LiveFeature.swift`-dədir.
 *
 * ⚠ CAVAB DÜZ (yastı) OBYEKTDİR və dəyər `null` OLA BİLƏR: tip `string | null`
 *   yazılmalıdır. Açarlar camelCase-ə çevrilmir — `features.live_enabled`
 *   nöqtəli açardır, ona görə indekslə oxunur.
 *
 * ⚠ SON MƏLUM DƏYƏR `localStorage`-a YAZILIR. NİYƏ: boot `Promise.all`-u
 *   `loadPublicSettings()`-i də GÖZLƏYİR (`main.ts` · `browser/main.ts`), yəni
 *   cavabsız kadr çəkilmir — keş «ilk kadr» üçün deyil, SORĞU ALINMAYAN açılış
 *   üçündür: 6 saniyəlik hədd dolanda app son bilinən dəyərlə açılır.
 */

/** `media/config` ilə eyni hədd — boot yolundakı sorğu ekranı gözlətməməlidir. */
const CONFIG_TIMEOUT_MS = 6000

const LIVE_KEY = 'kb_tv_live_enabled'

/** Açarın backend-dəki tam adı (`sys.settings` sətri). */
const LIVE_SETTING = 'features.live_enabled'

/** Serverin yastı cavabı — dəyər `null` ola bilir. */
type PublicSettings = { [key: string]: string | null }

let liveEnabled = readCached()

/**
 * Keşdən son məlum dəyər. `true`-dan başqa hər şey (yoxdur · null · «False» ·
 * saxlanc bağlıdır) BAĞLI deməkdir — yəni İLK AÇILIŞ həmişə bağlıdır.
 */
function readCached(): boolean {
  try {
    return window.localStorage.getItem(LIVE_KEY) === 'true'
  } catch {
    // TV-də saxlanc bağlıdırsa keş yoxdur — defolt qayda işləyir.
    return false
  }
}

function writeCached(on: boolean): void {
  try {
    window.localStorage.setItem(LIVE_KEY, on ? 'true' : 'false')
  } catch {
    // Saxlanc yoxdursa dəyər yalnız bu seans üçün yaddaşda qalır.
  }
}

/**
 * «Canlı» bölməsi menyuda görünürmü.
 *
 * ⚠ SİNXRONDUR: rail (`app.ts` → `railHidden`) və üst zolaq (`browser/shell.ts`)
 *   çəkilmə anında cavab gözləyə bilməz.
 */
export function isLiveEnabled(): boolean {
  return liveEnabled
}

/**
 * Boot-da BİR DƏFƏ çağırılır — `loadMediaConfig()` ilə eyni `Promise.all`
 * sətrində (yəni PARALEL, ardıcıl deyil).
 *
 * ⚠ HEÇ VAXT REJECT ETMİR: parametrlər alınmasa da app açılmalıdır (qara ekran
 *   qadağandır — CLAUDE.md §6); alınmayanda keşdəki son bilinən dəyər qalır
 *   (səbəbi faylın başındakı ikinci ⚠⚠ bloku).
 */
export function loadPublicSettings(): Promise<void> {
  return withTimeout(httpJson<PublicSettings>('/api/settings/public'), CONFIG_TIMEOUT_MS, 'settings/public')
    .then((settings) => {
      // ⚠ Cavab gəldi, açar YOXDURSA da qərar verilir: sahib sətri silibsə bu,
      //   «söndür» deməkdir — köhnə keşi saxlamaq bəndi diri qoyardı.
      const raw = settings ? settings[LIVE_SETTING] : null
      liveEnabled = raw === 'true'
      writeCached(liveEnabled)
    })
    .catch(() => {
      // ⚠ QƏSDƏN BOŞDUR: uğursuz sorğuda dəyər DƏYİŞMİR — keşdəki son bilinən
      //   dəyər qalır, keş heç vaxt yazılmayıbsa `readCached()` onsuz da `false`
      //   verib, yəni ilk açılış BAĞLIDIR. İzahı faylın başındakı ⚠⚠ blokdadır.
    })
}
